import React, { useState, useEffect } from 'react';
import './ChannelManagement.css';
import { fetchChannels, createChannel, updateChannel, deleteChannel, testChannel, fetchProjects } from '../services/api';

const ChannelManagement = () => {
  const [channels, setChannels] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData] = useState({
    channel_type: '',
    channel_name: '',
    channel_id: '',
    project: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadChannels();
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const result = await fetchProjects();
      
      if (result.success) {
        setProjects(result.projects);
      }
    } catch (error) {
      console.error('프로젝트 목록 로드 실패:', error);
    }
  };

  const loadChannels = async () => {
    try {
      setLoading(true);
      const result = await fetchChannels();
      
      if (result.success) {
        setChannels(result.channels);
      } else {
        setError(result.error || '채널 목록을 불러올 수 없습니다');
      }
    } catch (error) {
      console.error('채널 목록 로드 실패:', error);
      setError('채널 목록을 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChannel = async () => {
    if (!formData.channel_type || !formData.channel_name || !formData.channel_id) {
      setError('채널 종류, 채널명, 채널 ID는 필수 입력값입니다');
      return;
    }

    try {
      setLoading(true);
      const result = await createChannel(formData);
      
      if (result.success) {
        setShowAddModal(false);
        setFormData({ channel_type: '', channel_name: '', channel_id: '', project: '' });
        setError(null);
        await loadChannels();
      } else {
        setError(result.error || '채널 등록에 실패했습니다');
      }
    } catch (error) {
      console.error('채널 등록 실패:', error);
      setError('채널 등록에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleEditChannel = (channel) => {
    setEditingChannel(channel);
    setFormData({
      channel_type: channel.channel_type,
      channel_name: channel.channel_name,
      channel_id: channel.channel_id,
      project: channel.project || '',
      status: channel.status
    });
    setShowEditModal(true);
  };

  const handleUpdateChannel = async () => {
    try {
      setLoading(true);
      const result = await updateChannel(editingChannel.pk, {
        project: formData.project,
        status: formData.status
      });
      
      if (result.success) {
        setShowEditModal(false);
        setEditingChannel(null);
        setFormData({ channel_type: '', channel_name: '', channel_id: '', project: '', status: 'active' });
        setError(null);
        await loadChannels();
      } else {
        setError(result.error || '채널 수정에 실패했습니다');
      }
    } catch (error) {
      console.error('채널 수정 실패:', error);
      setError('채널 수정에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChannel = async (channelPk) => {
    if (!window.confirm('정말로 이 채널을 삭제하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      const result = await deleteChannel(channelPk);
      
      if (result.success) {
        await loadChannels();
      } else {
        setError(result.error || '채널 삭제에 실패했습니다');
      }
    } catch (error) {
      console.error('채널 삭제 실패:', error);
      setError('채널 삭제에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleTestChannel = async (channelPk, channelName) => {
    try {
      setLoading(true);
      const result = await testChannel(channelPk);
      
      if (result.success) {
        alert(`채널 테스트 성공: ${result.message}`);
      } else {
        setError(result.error || '채널 테스트에 실패했습니다');
      }
    } catch (error) {
      console.error('채널 테스트 실패:', error);
      setError('채널 테스트에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (channelType) => {
    switch (channelType) {
      case 'slack': return '💬';
      case 'telegram': return '✈️';
      default: return '📢';
    }
  };

  return (
    <div className="channel-management">
      <div className="channel-header">
        <h4>채널 관리</h4>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
        >
          + 채널 추가
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading && <div className="loading">로딩 중...</div>}

      <div className="channels-table">
        <table>
          <thead>
            <tr>
              <th>종류</th>
              <th>채널명</th>
              <th>채널 ID</th>
              <th>프로젝트</th>
              <th>상태</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">
                  등록된 채널이 없습니다
                </td>
              </tr>
            ) : (
              channels.map((channel) => (
                <tr key={channel.pk}>
                  <td>
                    <span className="channel-type">
                      {getChannelIcon(channel.channel_type)} {channel.channel_type}
                    </span>
                  </td>
                  <td>{channel.channel_name}</td>
                  <td><code>{channel.channel_id}</code></td>
                  <td>{channel.project || '-'}</td>
                  <td>
                    <span className={`status ${channel.status}`}>
                      {channel.status === 'active' ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon btn-edit"
                        onClick={() => handleEditChannel(channel)}
                        title="채널 수정"
                        disabled={loading}
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-icon btn-test"
                        onClick={() => handleTestChannel(channel.pk, channel.channel_name)}
                        title="채널 테스트"
                        disabled={loading}
                      >
                        🧪
                      </button>
                      <button 
                        className="btn-icon btn-delete"
                        onClick={() => handleDeleteChannel(channel.pk)}
                        title="채널 삭제"
                        disabled={loading}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 채널 추가 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>새 채널 추가</h3>
            
            <div className="form-group">
              <label>채널 종류 *</label>
              <select
                value={formData.channel_type}
                onChange={(e) => setFormData({...formData, channel_type: e.target.value})}
              >
                <option value="">선택하세요</option>
                <option value="slack">💬 Slack</option>
                <option value="telegram">✈️ Telegram</option>
              </select>
            </div>

            <div className="form-group">
              <label>채널명 *</label>
              <input
                type="text"
                value={formData.channel_name}
                onChange={(e) => setFormData({...formData, channel_name: e.target.value})}
                placeholder="채널명을 입력하세요"
              />
            </div>

            <div className="form-group">
              <label>채널 ID *</label>
              <input
                type="text"
                value={formData.channel_id}
                onChange={(e) => setFormData({...formData, channel_id: e.target.value})}
                placeholder="채널 ID를 입력하세요"
              />
            </div>

            <div className="form-group">
              <label>프로젝트</label>
              <select
                value={formData.project}
                onChange={(e) => setFormData({...formData, project: e.target.value})}
              >
                <option value="">선택안함</option>
                {projects.map((project) => (
                  <option key={project.pk} value={project.id}>
                    {project.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => {
                  setShowAddModal(false);
                  setFormData({ channel_type: '', channel_name: '', channel_id: '', project: '' });
                  setError(null);
                }}
              >
                취소
              </button>
              <button 
                className="btn btn-confirm"
                onClick={handleAddChannel}
                disabled={loading}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 채널 수정 모달 */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>채널 수정</h3>
            
            <div className="form-group">
              <label>프로젝트</label>
              <select
                value={formData.project}
                onChange={(e) => setFormData({...formData, project: e.target.value})}
              >
                <option value="">선택안함</option>
                {projects.map((project) => (
                  <option key={project.pk} value={project.id}>
                    {project.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>상태</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="active">활성</option>
                <option value="inactive">비활성</option>
              </select>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-cancel"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingChannel(null);
                  setFormData({ channel_type: '', channel_name: '', channel_id: '', project: '', status: 'active' });
                  setError(null);
                }}
              >
                취소
              </button>
              <button 
                className="btn btn-confirm"
                onClick={handleUpdateChannel}
                disabled={loading}
              >
                수정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelManagement;